import React, { useCallback, useMemo, useState } from 'react'
import {
  useStripe, Elements, useElements, PaymentElement, ExpressCheckoutElement,
} from '@stripe/react-stripe-js'
import { Stripe, StripeElements, StripeExpressCheckoutElementConfirmEvent, loadStripe } from '@stripe/stripe-js'
import { STRIPE_PUBLIC_KEY } from 'Config/constants'
import Button from '@components/Button'
import UiButton from '@components/ui-v2/Button'
import { formatApiPrice } from 'Helpers/format-helper'
import { notify } from 'Actions/notifications'
import { useDispatch } from 'react-redux'

export type StripePaymentElementProps = {
  clientSecret: string
  amount?: number
  currency?: string
  stripeAccount: string | null
  returnUrl?: string
  onPaymentSuccess?: (param: { paymentMethodId?: string, paymentIntentId: string, stripe: Stripe, elements?: StripeElements }) => Promise<void>
  onPaymentMeanSuccess?: (param: { paymentMethodId: string, stripe: Stripe }) => Promise<void>
  close: () => void
  onClick?: () => void
  onError?: (errorMessage?: string) => void
  isOffSession?: boolean
  // `ui-v2` : bouton noir du catalogue ui-v2 (tunnel de création), `legacy` (défaut) : bouton jaune
  // de l'espace client.
  buttonStyle?: 'legacy' | 'ui-v2'
}

const StripePaymentElementForm = ({
  amount, currency, onPaymentSuccess, onPaymentMeanSuccess, returnUrl, isOffSession, onClick, onError,
  buttonStyle = 'legacy',
}: Pick<StripePaymentElementProps, 'amount' | 'currency' | 'onPaymentSuccess' | 'onPaymentMeanSuccess' | 'returnUrl' | 'isOffSession' | 'onClick' | 'onError' | 'buttonStyle'>) => {
  const stripe = useStripe()
  const elements = useElements()

  const formattedPrice = useMemo(() => (amount && currency) && formatApiPrice(amount, currency), [amount, currency])
  const buttonLabel = amount ? `Payer ${formattedPrice}` : 'Valider'
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>()

  const validatePaymentMethod = async () => {
    if (!stripe || !elements || !onPaymentMeanSuccess) return
    const res = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: returnUrl, // no need to really handle for CB, will never be redirected
      },
    })
    console.log('res confirmSetup --------------------------------->')
    console.log(res)

    const { error, setupIntent } = res

    if (error) {
      setErrorMessage(error.message)
      if (onError) onError(error.message)
    } else {
      await onPaymentMeanSuccess({ paymentMethodId: setupIntent.payment_method, stripe })
    }
  }

  const validatePayment = async () => {
    if (!stripe || !elements || !onPaymentSuccess) return

    const result = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: returnUrl,
      },
    })

    const { error, paymentIntent } = result

    console.log('paymentIntent --------------------------------->')
    console.log(result)

    if (error) {
      setErrorMessage(error.message)
      if (onError) onError(error.message)
    } else {
      await onPaymentSuccess({ paymentMethodId: paymentIntent.payment_method, paymentIntentId: paymentIntent.id, stripe, elements })
    }
  }

  const handleSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    if (onClick) onClick()
    setIsLoading(true)
    event.preventDefault()

    if (isOffSession) {
      await validatePaymentMethod()
    } else {
      await validatePayment()
    }

    setIsLoading(false)
  }, [elements, stripe, isOffSession, onClick, onError, returnUrl, onPaymentSuccess, onPaymentMeanSuccess])

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement
        options={{
          defaultValues: {
            card: {
              network: ['visa', 'mastercard', 'cartes_bancaires'],
            },
          },
          terms: {
            card: 'never',
          },
        }}
      />
      {buttonStyle === 'ui-v2' ? (
        <UiButton
          isDisabled={!stripe}
          type="submit"
          isLoading={isLoading}
          isBlock
          className="mt-4"
        >
          {buttonLabel}
        </UiButton>
      ) : (
        <Button
          isDisabled={!stripe}
          type="submit"
          isLoading={isLoading}
          className="mt-4"
        >
          {buttonLabel}
        </Button>
      )}
      {errorMessage && <div className="mt-4 text-pastel-red">{errorMessage}</div>}
    </form>
  )
}

const StripePaymentElement = ({
  clientSecret, amount, currency, stripeAccount, onPaymentSuccess, onPaymentMeanSuccess, returnUrl, close, isOffSession, onClick, onError,
  buttonStyle,
}: StripePaymentElementProps) => {
  const stripePromise = useMemo(() => loadStripe(
    STRIPE_PUBLIC_KEY,
    {
      locale: 'fr-FR',
      ...(stripeAccount ? { stripeAccount } : {}),
    },
  ), [stripeAccount])

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <StripePaymentElementForm
        amount={amount}
        currency={currency}
        onPaymentSuccess={async (param) => {
          try {
            if (onPaymentSuccess) {
              await onPaymentSuccess(param)
            }
          } finally {
            close()
          }
        }}
        onPaymentMeanSuccess={async (param) => {
          try {
            if (onPaymentMeanSuccess) {
              await onPaymentMeanSuccess(param)
            }
          } finally {
            close()
          }
        }}
        onClick={onClick}
        onError={onError}
        returnUrl={returnUrl}
        isOffSession={isOffSession}
        buttonStyle={buttonStyle}
      />
    </Elements>
  )
}

export const StripeExpressPaymentElementForm = (
  { onPaymentSuccess, onError, returnUrl }: Pick<StripePaymentElementProps, 'onPaymentSuccess' | 'onError' | 'returnUrl'>
) => {
  const dispatch = useDispatch()
  const stripe = useStripe()
  const elements = useElements()

  const onExpressPaymentConfirm = async (e: StripeExpressCheckoutElementConfirmEvent) => {
    console.log('stripe --------------------------------->')
    console.log(stripe)
    console.log(elements)
    console.log(e)

    if (!stripe || !elements) return

    const { error, paymentIntent } = await stripe.confirmPayment({
      // `Elements` instance that was used to create the Payment Element
      elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: returnUrl,
      },
    })

    console.log('paymentIntent --------------------------------->')
    console.log(error)
    console.log(paymentIntent)

    if (error) {
      dispatch(notify('error', error.message))
      onError && onError(error.message)
    } else {
      onPaymentSuccess && onPaymentSuccess({ paymentIntentId: paymentIntent.id })
    }
  }

  return (
    <ExpressCheckoutElement
      onConfirm={onExpressPaymentConfirm}
      options={{
        buttonType: {
          applePay: 'plain',
          googlePay: 'plain',
        },
        wallets: {
          applePay: 'always',
          googlePay: 'always',
        }
      }}
    />
  )
}

export const StripeExpressPaymentElement = ({
  clientSecret, stripeAccount, onPaymentSuccess, returnUrl, onError
}: StripePaymentElementProps) => {
  const stripePromise = useMemo(() => loadStripe(
    STRIPE_PUBLIC_KEY,
    {
      locale: 'fr-FR',
      ...(stripeAccount ? { stripeAccount } : {}),
    },
  ), [stripeAccount])

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <StripeExpressPaymentElementForm
        onPaymentSuccess={onPaymentSuccess}
        returnUrl={returnUrl}
        onError={onError}
      />
    </Elements>
  )
}

export default StripePaymentElement
